import { createSelector, createSelectorFactory, defaultMemoize } from '@ngrx/store';

import { UserIdMap, UserSelectors } from '@core/account-data';
import { DepartmentIdMap, DepartmentSelectors } from '@core/department-data';
import { DevFlagSelectors } from '@core/dev-flags-data';
import { DevFlag } from '@core/dev-flags-types';
import { LocationSelectors } from '@core/locations-data';
import type { Location } from '@core/locations-types';
import { memoizeSelector } from '@core/ngrx-legacy';
import {
  JobBasic,
  JobItem,
  JobItemsIdMap,
  OfferBrief,
  OfferItem,
  type OfferStatus,
} from '@core/offers-types';
import { Stage } from '@core/stages-types';

import { MapperService } from '../../../services/mapper.service';
import { StageSelectors } from '../../stages.store';
import { JobsBasicSelectors } from '../jobs-basic/jobs-basic.store';
import { JobsBriefSelectors } from '../jobs-brief/jobs-brief.store';
import { JobItemsSelectors } from '../jobs-item/jobs-item.store';

type AdditionalDataMap = {
  departmentsIdMap: DepartmentIdMap;
  usersIdMap: UserIdMap;
  locationsIdMap: Record<number, Location>;
  allStages: Stage[];
};

const hasNewForceNewOffersEndpointFlag = DevFlagSelectors.isEnabled(
  DevFlag.ForceNewOffersEndpointFlag,
);

const jobItemAdditionalData = createSelector(
  DepartmentSelectors.idMap,
  UserSelectors.idMap,
  LocationSelectors.idMap,
  StageSelectors.allEntities,
  (departmentsIdMap, usersIdMap, locationsIdMap, allStages) => ({
    departmentsIdMap,
    usersIdMap,
    locationsIdMap,
    allStages,
  }),
);

const mapJobBasicToJobItem = (job: JobBasic, additionalDataMap: AdditionalDataMap) => {
  const { departmentsIdMap, usersIdMap, locationsIdMap, allStages } = additionalDataMap;
  const department = departmentsIdMap[job?.departmentId || -1] || null;
  const recruiter = job?.recruiterId ? usersIdMap[job.recruiterId] || null : null;
  const hiringManager = job?.hiringManagerId ? usersIdMap[job.hiringManagerId] || null : null;
  const locations = (job.locationIds || [])
    .map(locationId => locationsIdMap[locationId])
    .filter(location => !!location);
  const stages = allStages.filter(stage => stage.pipelineTemplateId === job.pipelineTemplateId);
  const pipelineTemplate = stages.length
    ? {
        id: job.pipelineTemplateId,
        stages,
      }
    : null;
  return MapperService.mapJobBasicToJobItem(job, {
    department,
    recruiter,
    hiringManager,
    locations,
    pipelineTemplate,
  });
};
const mapOfferItemToJobItem = (job: OfferItem, additionalDataMap: AdditionalDataMap) => {
  const { departmentsIdMap, usersIdMap, locationsIdMap, allStages } = additionalDataMap;
  const department = departmentsIdMap[job?.departmentId || -1] || null;
  const recruiter = job?.recruiterId ? usersIdMap[job.recruiterId] || null : null;
  const hiringManager = job?.hiringManagerId ? usersIdMap[job.hiringManagerId] || null : null;
  const locations = (job.locationIds || [])
    .map(locationId => locationsIdMap[locationId])
    .filter(location => !!location);
  const stages = allStages.filter(stage => stage.pipelineTemplateId === job.pipelineTemplateId);
  const pipelineTemplate = stages.length
    ? {
        id: job.pipelineTemplateId!,
        stages,
      }
    : null;
  return MapperService.mapOfferItemToJobItem(job, {
    department,
    recruiter,
    hiringManager,
    locations,
    pipelineTemplate,
  });
};
const mapOfferBriefToJobItem = (job: OfferBrief, additionalDataMap: AdditionalDataMap) => {
  const { departmentsIdMap, usersIdMap, locationsIdMap, allStages } = additionalDataMap;
  const department = departmentsIdMap[job?.departmentId || -1] || null;
  const recruiter = job?.recruiterId ? usersIdMap[job.recruiterId] || null : null;
  const hiringManager = job?.hiringManagerId ? usersIdMap[job.hiringManagerId] || null : null;
  const locations = (job.locationIds || [])
    .map(locationId => locationsIdMap[locationId])
    .filter(location => !!location);
  const stages = allStages.filter(stage => stage.pipelineTemplateId === job.pipelineTemplateId);
  const pipelineTemplate = stages.length
    ? {
        id: job.pipelineTemplateId!,
        stages,
      }
    : null;
  return MapperService.mapOfferBriefToJobItem(job, {
    department,
    recruiter,
    hiringManager,
    locations,
    pipelineTemplate,
  });
};

const activeJobItems = createSelector(
  hasNewForceNewOffersEndpointFlag,
  JobsBasicSelectors.active,
  JobItemsSelectors.active,
  jobItemAdditionalData,
  (hasNewForceNewOffersEndpointFlag, jobsBasicActive, jobItemsActive, additionalData) =>
    hasNewForceNewOffersEndpointFlag
      ? jobsBasicActive.map(job => mapJobBasicToJobItem(job, additionalData))
      : jobItemsActive.map(job => mapOfferItemToJobItem(job, additionalData)),
);

const activeJobItemsIsLoaded = createSelector(
  hasNewForceNewOffersEndpointFlag,
  JobsBasicSelectors.isLoaded(),
  JobItemsSelectors.activeScopeIsLoaded,
  (hasNewForceNewOffersEndpointFlag, jobBasicIsLoaded, jobItemsActiveIsLoaded) =>
    hasNewForceNewOffersEndpointFlag ? jobBasicIsLoaded : jobItemsActiveIsLoaded,
);

const activeJobItemsByNotStatus = memoizeSelector((statuses: OfferStatus[]) =>
  createSelector(
    hasNewForceNewOffersEndpointFlag,
    JobsBasicSelectors.activeByNotStatus(statuses),
    JobItemsSelectors.activeByNotStatus(statuses),
    jobItemAdditionalData,
    (hasNewForceNewOffersEndpointFlag, jobBasics, jobItems, additionalData) =>
      hasNewForceNewOffersEndpointFlag
        ? jobBasics.map(job => mapJobBasicToJobItem(job, additionalData))
        : jobItems.map(job => mapOfferItemToJobItem(job, additionalData)),
  ),
);

const activeJobItemsCanAssignTo = createSelector(
  hasNewForceNewOffersEndpointFlag,
  JobsBasicSelectors.canAssignTo,
  JobItemsSelectors.canAssignTo,
  jobItemAdditionalData,
  (hasNewForceNewOffersEndpointFlag, jobBasics, jobItems, additionalData) =>
    hasNewForceNewOffersEndpointFlag
      ? jobBasics.map(job => mapJobBasicToJobItem(job, additionalData))
      : jobItems.map(job => mapOfferItemToJobItem(job, additionalData)),
);

const activeJobItemsByStatus = memoizeSelector((statuses: OfferStatus[]) =>
  createSelector(
    hasNewForceNewOffersEndpointFlag,
    JobsBasicSelectors.activeByStatus(statuses),
    JobItemsSelectors.activeByStatus(statuses),
    jobItemAdditionalData,
    (hasNewForceNewOffersEndpointFlag, jobBasics, jobItems, additionalData) =>
      hasNewForceNewOffersEndpointFlag
        ? jobBasics.map(job => mapJobBasicToJobItem(job, additionalData))
        : jobItems.map(job => mapOfferItemToJobItem(job, additionalData)),
  ),
);

const archivedJobItems = createSelector(
  hasNewForceNewOffersEndpointFlag,
  JobsBasicSelectors.archived,
  JobsBriefSelectors.archived,
  jobItemAdditionalData,
  (hasNewForceNewOffersEndpointFlag, jobsBasicArchived, jobsBriefArchived, additionalData) =>
    hasNewForceNewOffersEndpointFlag
      ? jobsBasicArchived.map(job => mapJobBasicToJobItem(job, additionalData))
      : jobsBriefArchived.map(job => mapOfferBriefToJobItem(job, additionalData)),
);

const jobItems = createSelector(activeJobItems, archivedJobItems, (active, archived) => [
  ...active,
  ...archived,
]);

const jobItemsIdMap = createSelector(jobItems, jobs =>
  jobs.reduce(
    (acc, job) => {
      acc[job.id] = job;
      return acc;
    },
    <JobItemsIdMap>{},
  ),
);

const jobItemById = memoizeSelector((id: number) =>
  createSelector(
    hasNewForceNewOffersEndpointFlag,
    JobsBasicSelectors.byIdWithReferences(id),
    JobsBriefSelectors.byIdWithReferences(id),
    JobItemsSelectors.byIdWithReferences(id),
    jobItemAdditionalData,
    (hasNewForceNewOffersEndpointFlag, jobBasic, jobBrief, jobItem, additionalData) => {
      if (hasNewForceNewOffersEndpointFlag) {
        return jobBasic ? mapJobBasicToJobItem(jobBasic, additionalData) : jobBasic;
      } else if (jobItem) {
        return mapOfferItemToJobItem(jobItem, additionalData);
      } else {
        return jobBrief ? mapOfferBriefToJobItem(jobBrief, additionalData) : jobBrief;
      }
    },
  ),
);

const jobItemBySlug = memoizeSelector((slug: string) =>
  createSelector(
    hasNewForceNewOffersEndpointFlag,
    JobsBasicSelectors.bySlug(slug),
    JobsBriefSelectors.bySlug(slug),
    JobItemsSelectors.bySlug(slug),
    jobItemAdditionalData,
    (hasNewForceNewOffersEndpointFlag, jobBasic, jobBrief, jobItem, additionalData) => {
      if (hasNewForceNewOffersEndpointFlag) {
        return jobBasic ? mapJobBasicToJobItem(jobBasic, additionalData) : jobBasic;
      } else if (jobItem) {
        return mapOfferItemToJobItem(jobItem, additionalData);
      } else {
        return jobBrief ? mapOfferBriefToJobItem(jobBrief, additionalData) : jobBrief;
      }
    },
  ),
);

const jobItemsByIds = memoizeSelector((ids: number[]) =>
  createSelectorFactory(defaultMemoize)(
    ...(ids || []).map(id => jobItemById(id)),
    (...result: JobItem[]) => result.filter(job => !!job),
  ),
);

const selectors = {
  jobItems,
  jobItemsIdMap,
  jobItemById,
  jobItemBySlug,
  jobItemsByIds,
  activeJobItems,
  activeJobItemsIsLoaded,
  activeJobItemsByNotStatus,
  activeJobItemsByStatus,
  activeJobItemsCanAssignTo,
  archivedJobItems,
};

export { selectors as JobsWrapperSelectors };
